// Sub-position within the `'from'` clause: did the cursor land at the JOIN
// TARGET slot — i.e. immediately after a `JOIN` keyword (or the introducer
// keywords that precede a JOIN target, like `APPLY` or `LATERAL`)?
//
//   "SELECT * FROM x JOIN |"           ← JOIN target slot → true
//   "SELECT * FROM x INNER JOIN |"     ← JOIN target slot → true
//   "SELECT * FROM x CROSS APPLY |"    ← T-SQL JOIN target  → true
//   "SELECT * FROM x, LATERAL |"       ← PG / Oracle JOIN target → true
//   "SELECT * FROM "                   ← bare FROM list → false
//   "SELECT * FROM x, "                ← bare FROM list (comma-separated) → false
//   "SELECT * FROM |rental r"          ← bare FROM list (cursor before bare relation) → false
//
// Used to gate FK-driven JOIN snippet emission. The snippet generator's
// contract is "emit a JOIN target with an ON clause"; at a bare-FROM-list
// slot the snippet doesn't form a valid syntactic continuation (the user
// would end up with `FROM rental r, customer c ON ...`, which is illegal in
// every dialect). Demoting/suppressing the snippet here stops it from
// crowding out plain table names that the user actually wanted.
//
// This module is a pure text scan — it does not consult the grammar parse
// tree, so it can run on the resolver's hot path without paying for a parse.

/** Keywords that, when the last token before the cursor, mean the cursor is
 *  at a JOIN target slot. `JOIN` covers `INNER/LEFT/RIGHT/FULL/CROSS/OUTER
 *  JOIN`; `APPLY` covers T-SQL `CROSS APPLY` / `OUTER APPLY`; `LATERAL`
 *  covers PG / Oracle / MySQL 8.0.14+ lateral derived tables. */
const JOIN_TARGET_KEYWORDS: ReadonlySet<string> = new Set([
  'JOIN',
  'APPLY',
  'LATERAL',
]);

/**
 * Returns `true` when the cursor sits immediately after a JOIN-introducing
 * keyword inside the FROM clause, i.e. at a slot where the user is naming
 * the right-hand side of a JOIN.
 *
 * Caller must have already confirmed `positionKind === 'from'`. The function
 * does not validate clause context itself — it only inspects the last
 * non-whitespace token before the cursor.
 *
 * Mid-identifier positions return `false` (the user is still typing a name).
 *
 *   "SELECT * FROM x JOIN " → true
 *   "SELECT * FROM x INNER JOIN " → true
 *   "SELECT * FROM x CROSS APPLY " → true
 *   "SELECT * FROM x, LATERAL " → true
 *   "SELECT * FROM " → false (last token is FROM, bare list)
 *   "SELECT * FROM x, " → false (last token is comma, bare list)
 *   "SELECT * FROM x" → false (no trailing whitespace — user is mid-type)
 *   "SELECT * FROM x JOIN" → false (no trailing whitespace — user is mid-type)
 */
export function isAtJoinTargetSlot(
  text: string,
  offset: number,
  statementStart: number,
): boolean {
  let i = Math.min(offset, text.length);
  // Skip whitespace immediately before the cursor.
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  // No whitespace before cursor → user is mid-identifier; cannot be at the
  // JOIN target slot (which requires whitespace after the JOIN keyword).
  if (i === offset) return false;
  if (i <= statementStart) return false;

  const prevChar = text[i - 1] ?? '';

  // Only bare keyword tokens can be JOIN-introducers. A closing identifier
  // delimiter (`]`, `"`, `` ` ``), a comma, a paren, etc. is never a JOIN
  // introducer.
  if (!/[A-Za-z0-9_]/.test(prevChar)) return false;

  // Walk back to extract the trailing identifier-ish word.
  let j = i;
  while (j > statementStart && /[A-Za-z0-9_]/.test(text[j - 1] ?? '')) j -= 1;
  const word = text.slice(j, i).toUpperCase();
  return JOIN_TARGET_KEYWORDS.has(word);
}
